import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { RefreshCw, Save, Trash2, Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const hAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('cap_token')}` } });

// 14 avatar styles: solid, gradient, geometric, glow, bordered — all using user initials
const AVATARS = [
  // Solid
  { id: 'solid-emerald', bg: '#00e5a0', textColor: '#0d0f14' },
  { id: 'solid-blue', bg: '#4f8eff', textColor: '#fff' },
  { id: 'solid-purple', bg: '#7c3aed', textColor: '#fff' },
  // Linear gradients
  { id: 'gradient-pink-red', bg: 'linear-gradient(135deg,#ec4899 0%,#ef4444 100%)', textColor: '#fff' },
  { id: 'gradient-cyan-blue', bg: 'linear-gradient(135deg,#22d3ee 0%,#4f8eff 100%)', textColor: '#fff' },
  { id: 'gradient-purple-pink', bg: 'linear-gradient(135deg,#7c3aed 0%,#ec4899 100%)', textColor: '#fff' },
  { id: 'gradient-emerald-cyan', bg: 'linear-gradient(135deg,#00e5a0 0%,#22d3ee 100%)', textColor: '#0d0f14' },
  { id: 'gradient-gold-orange', bg: 'linear-gradient(135deg,#fbbf24 0%,#f97316 100%)', textColor: '#0d0f14' },
  // Geometric / conic
  { id: 'geo-rainbow', bg: 'conic-gradient(from 0deg,#7c3aed,#ec4899,#f97316,#fbbf24,#00e5a0,#22d3ee,#7c3aed)', textColor: '#fff' },
  { id: 'geo-radial-emerald', bg: 'radial-gradient(circle at 30% 30%,#00e5a0 0%,#1e293b 100%)', textColor: '#fff' },
  // Bordered (dark center, colored ring)
  { id: 'border-gold', bg: '#0d0f14', border: '#fbbf24', textColor: '#fbbf24' },
  { id: 'border-cyan', bg: '#0d0f14', border: '#22d3ee', textColor: '#22d3ee' },
  // Glow
  { id: 'glow-emerald', bg: '#00e5a0', textColor: '#0d0f14', glow: '#00e5a0' },
  { id: 'glow-rose', bg: '#ec4899', textColor: '#fff', glow: '#ec4899' },
];

const getAvatar = (id) => AVATARS.find(a => a.id === id) || AVATARS[0];

const AvatarVisual = ({ avatar, initials, size = 12, selected = false }) => {
  const px = size * 4; // tailwind units to px (w-12 = 48px)
  const style = {
    width: px,
    height: px,
    background: avatar.bg,
    color: avatar.textColor,
    border: avatar.border ? `2px solid ${avatar.border}` : 'none',
    boxShadow: avatar.glow ? `0 0 16px ${avatar.glow}80, 0 0 4px ${avatar.glow}` : 'none',
  };
  return (
    <div className="rounded-full flex items-center justify-center font-bold transition-all shrink-0" style={style}>
      <span className={size >= 16 ? 'text-xl' : 'text-sm'}>{initials}</span>
    </div>
  );
};

const AvatarPicker = ({ username, current, onChange }) => {
  const initials = (username || '?').slice(0, 2).toUpperCase();
  return (
    <div className="grid grid-cols-7 gap-2 sm:gap-3" data-testid="avatar-picker">
      {AVATARS.map(a => {
        const isSelected = current === a.id;
        return (
          <button
            key={a.id}
            type="button"
            onClick={() => onChange(a.id)}
            className={`relative rounded-full transition-all ${isSelected ? 'scale-110 ring-2 ring-[#00e5a0] ring-offset-2 ring-offset-[#1a1d27]' : 'opacity-70 hover:opacity-100 hover:scale-105'}`}
            data-testid={`avatar-option-${a.id}`}
            aria-label={`Avatar ${a.id}`}
          >
            <AvatarVisual avatar={a} initials={initials} size={12} />
            {isSelected && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#00e5a0] rounded-full flex items-center justify-center text-[10px] text-[#0d0f14] font-bold">✓</span>
            )}
          </button>
        );
      })}
    </div>
  );
};

const SectionCard = ({ title, children, delay = 0 }) => (
  <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }} className="bg-[#1a1d27] border border-white/5 rounded-xl p-6">
    <h2 className="font-display text-base font-semibold text-white mb-5">{title}</h2>
    {children}
  </motion.div>
);

const ProfilePage = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showOldPwd, setShowOldPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);

  // Form state
  const [form, setForm] = useState({
    username: '', bio: '', avatar: '', currency: 'EUR', investor_style: '',
    notif_likes: true, notif_answers: true, notif_badges: true, notif_wazzl: true,
  });
  const [emailForm, setEmailForm] = useState('');
  const [pwdForm, setPwdForm] = useState({ old: '', new: '', confirm: '' });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const settingsRes = await axios.get(`${API}/user/settings`, hAuth());
      const s = settingsRes.data;
      setForm({
        username: s.username || '', bio: s.bio || '', avatar: s.avatar || '',
        currency: s.currency || 'EUR', investor_style: s.investor_style || '',
        notif_likes: s.notif_likes ?? true, notif_answers: s.notif_answers ?? true,
        notif_badges: s.notif_badges ?? true, notif_wazzl: s.notif_wazzl ?? true,
      });
      setEmailForm(s.email || '');
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/user/settings`, form, hAuth());
      toast.success('Profil mis à jour');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleChangeEmail = async () => {
    try {
      await axios.put(`${API}/user/email`, { new_email: emailForm }, hAuth());
      toast.success('Email mis à jour');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleChangePwd = async () => {
    if (pwdForm.new !== pwdForm.confirm) { toast.error('Les mots de passe ne correspondent pas'); return; }
    if (pwdForm.new.length < 6) { toast.error('Minimum 6 caractères'); return; }
    try {
      await axios.put(`${API}/user/password`, { old_password: pwdForm.old, new_password: pwdForm.new }, hAuth());
      toast.success('Mot de passe modifié');
      setPwdForm({ old: '', new: '', confirm: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await axios.delete(`${API}/user/account`, hAuth());
      toast.success('Compte supprimé');
      logout();
      navigate('/');
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-full"><RefreshCw className="w-8 h-8 text-cap-emerald animate-spin" /></div>;

  const initials = (form.username || '?').slice(0, 2).toUpperCase();
  const inputCls = "w-full bg-[#0d0f14] border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-[#00e5a0]/50 transition-colors";

  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto space-y-6" data-testid="profile-page">
      <h1 className="font-display text-3xl font-bold text-white mb-1">Mon Profil</h1>
      <p className="text-cap-text-secondary text-sm">Gérez vos informations personnelles et vos préférences</p>

      {/* ═══ BLOC 1 — Identité ═══ */}
      <SectionCard title="Identité" delay={0}>
        <div className="space-y-5">
          {/* Avatar */}
          <div>
            <label className="block text-xs text-cap-text-muted mb-2">Photo de profil</label>
            <div className="flex items-start gap-5 flex-wrap">
              <div data-testid="profile-avatar">
                <AvatarVisual avatar={getAvatar(form.avatar)} initials={initials} size={16} />
              </div>
              <AvatarPicker username={form.username} current={form.avatar} onChange={v => setForm(f => ({ ...f, avatar: v }))} />
            </div>
          </div>
          {/* Username */}
          <div>
            <label className="block text-xs text-cap-text-muted mb-1">Pseudo / nom d'utilisateur</label>
            <input className={inputCls} value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} maxLength={30} data-testid="profile-username" />
          </div>
          {/* Bio */}
          <div>
            <label className="block text-xs text-cap-text-muted mb-1">Bio courte <span className="text-cap-text-muted/50">({form.bio.length}/150)</span></label>
            <textarea className={`${inputCls} resize-none h-20`} value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value.slice(0, 150) }))} maxLength={150} placeholder="Décrivez-vous en quelques mots..." data-testid="profile-bio" />
          </div>
        </div>
      </SectionCard>

      {/* ═══ BLOC 2 — Préférences ═══ */}
      <SectionCard title="Préférences" delay={0.05}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="block text-xs text-cap-text-muted mb-1">Devise principale</label>
            <select className={inputCls} value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))} data-testid="profile-currency">
              <option value="EUR">EUR</option>
              <option value="USD">USD</option>
              <option value="CHF">CHF</option>
              <option value="GBP">GBP</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-cap-text-muted mb-1">Profil d'investisseur</label>
            <div className="flex gap-2">
              {['Prudent', 'Équilibré', 'Dynamique'].map(s => (
                <button key={s} onClick={() => setForm(f => ({ ...f, investor_style: s.toLowerCase() }))}
                  className={`flex-1 text-xs py-2 rounded-lg border transition-all ${form.investor_style === s.toLowerCase() ? 'border-[#00e5a0] bg-[#00e5a0]/10 text-[#00e5a0]' : 'border-white/10 text-cap-text-muted hover:border-white/20'}`}
                  data-testid={`style-${s.toLowerCase()}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      </SectionCard>

      {/* ═══ BLOC 3 — Compte & Sécurité ═══ */}
      <SectionCard title="Compte & Sécurité" delay={0.1}>
        <div className="space-y-6">
          {/* Email */}
          <div>
            <label className="block text-xs text-cap-text-muted mb-1">Email</label>
            <div className="flex gap-2">
              <input className={`${inputCls} flex-1`} type="email" value={emailForm} onChange={e => setEmailForm(e.target.value)} data-testid="profile-email" />
              <Button size="sm" className="bg-white/5 border border-white/10 text-white hover:bg-white/10 text-xs h-9 px-4" onClick={handleChangeEmail} data-testid="save-email-btn">Modifier</Button>
            </div>
          </div>
          {/* Password */}
          <div>
            <label className="block text-xs text-cap-text-muted mb-2">Modifier le mot de passe</label>
            <div className="space-y-2">
              <div className="relative">
                <input className={inputCls} type={showOldPwd ? 'text' : 'password'} placeholder="Ancien mot de passe" value={pwdForm.old} onChange={e => setPwdForm(f => ({ ...f, old: e.target.value }))} data-testid="old-password" />
                <button className="absolute right-3 top-1/2 -translate-y-1/2 text-cap-text-muted hover:text-white" onClick={() => setShowOldPwd(!showOldPwd)}>
                  {showOldPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="relative">
                <input className={inputCls} type={showNewPwd ? 'text' : 'password'} placeholder="Nouveau mot de passe" value={pwdForm.new} onChange={e => setPwdForm(f => ({ ...f, new: e.target.value }))} data-testid="new-password" />
                <button className="absolute right-3 top-1/2 -translate-y-1/2 text-cap-text-muted hover:text-white" onClick={() => setShowNewPwd(!showNewPwd)}>
                  {showNewPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <input className={inputCls} type="password" placeholder="Confirmer le nouveau mot de passe" value={pwdForm.confirm} onChange={e => setPwdForm(f => ({ ...f, confirm: e.target.value }))} data-testid="confirm-password" />
              <Button size="sm" className="bg-white/5 border border-white/10 text-white hover:bg-white/10 text-xs h-9 px-4" onClick={handleChangePwd} disabled={!pwdForm.old || !pwdForm.new} data-testid="save-pwd-btn">Changer le mot de passe</Button>
            </div>
          </div>
          {/* Delete account */}
          <div className="pt-4 border-t border-white/5">
            <Button variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300 text-xs" onClick={() => setShowDeleteConfirm(true)} data-testid="delete-account-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Supprimer mon compte
            </Button>
          </div>
        </div>
      </SectionCard>

      {/* ═══ BLOC 4 — Notifications ═══ */}
      <SectionCard title="Notifications" delay={0.15}>
        <div className="space-y-4">
          {[
            { key: 'notif_likes', label: 'Likes sur mes thèses' },
            { key: 'notif_answers', label: 'Réponses à mes questions' },
            { key: 'notif_badges', label: 'Nouveaux badges débloqués' },
            { key: 'notif_wazzl', label: 'Messages de @wazzl' },
          ].map(n => (
            <div key={n.key} className="flex items-center justify-between py-1">
              <span className="text-sm text-cap-text-secondary">{n.label}</span>
              <Switch checked={form[n.key]} onCheckedChange={v => setForm(f => ({ ...f, [n.key]: v }))} data-testid={`toggle-${n.key}`} />
            </div>
          ))}
        </div>
      </SectionCard>

      {/* ═══ SAVE BUTTON ═══ */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
        <Button className="w-full bg-[#00e5a0] text-[#0d0f14] font-semibold hover:bg-[#00e5a0]/90 h-11" onClick={handleSave} disabled={saving} data-testid="save-profile-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer
        </Button>
      </motion.div>

      {/* ═══ DELETE ACCOUNT DIALOG ═══ */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="bg-[#1a1d27] border-white/10 text-white max-w-sm">
          <DialogHeader><DialogTitle>Supprimer mon compte</DialogTitle></DialogHeader>
          <p className="text-cap-text-secondary text-sm mt-2">Cette action est irréversible, toutes vos données seront supprimées.</p>
          <div className="flex items-center justify-end gap-3 mt-4">
            <Button variant="outline" size="sm" className="border-white/10 text-cap-text-muted hover:text-white" onClick={() => setShowDeleteConfirm(false)} data-testid="cancel-delete-user">Annuler</Button>
            <Button size="sm" className="bg-red-600 text-white hover:bg-red-700" onClick={handleDeleteAccount} data-testid="confirm-delete-user">Confirmer</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProfilePage;
